import ComponentPlaceholder from 'components/ComponentPlaceholder';
import { getValidSitecoreLink } from 'components/SitecoreLink';
import { useIsEditing } from 'hooks';
import type { DigizuiteAsset } from 'models/asset';
import type { ProductCard } from 'models/productCard';
import type {
	ColorField,
	Field,
	SitecoreBlockProps,
	SitecoreLink,
} from 'models/sitecore';
import type { HTMLString, JSONObject } from 'types';
import { getEditorMargin } from 'utils/business-logic';
import { getAsset, is } from 'utils/helpers';

import InspirationLandscape, {
	type VerticalAlign,
} from './InspirationLandscape';

interface Props extends SitecoreBlockProps {
	fields: {
		backgroundColor?: ColorField;
		body?: JSONObject;
		campaignBubbleLabel?: Field<string>;
		campaignBubbleSale?: Field<boolean>;
		campaignBubbleText?: Field<string>;
		content?: Field<HTMLString>;
		datasourceName?: string;
		/** @deprecated Replaced by `content` */
		description?: Field<string>;
		heading?: Field<string>;
		isCampaignSpinner?: Field<boolean>;
		isWide?: Field<boolean>;
		mirror?: Field<boolean>;
		preventAutoplay?: Field<boolean>;
		primaryImage?: Field<DigizuiteAsset[]>;
		primaryImageTall?: Field<DigizuiteAsset[]>;
		primaryLink?: Field<SitecoreLink>;
		products?: ProductCard[];
		quaternaryLink?: Field<SitecoreLink>;
		secondaryLink?: Field<SitecoreLink>;
		tertiaryLink?: Field<SitecoreLink>;
		textBackgroundColor?: ColorField;
		verticalAlign?: Field<VerticalAlign>;
		video?: Field<DigizuiteAsset[]>;
	};
}

export default function InspirationLandscapeBlock({ fields, params }: Props) {
	const {
		content,
		datasourceName,
		description,
		heading,
		primaryLink,
		quaternaryLink,
		secondaryLink,
		tertiaryLink,
		verticalAlign,
	} = fields;
	const primaryImg = getAsset('Templated 2:1', fields.primaryImage);
	const primaryImgTall = getAsset('Templated 4:3', fields.primaryImageTall);

	const isEditing = useIsEditing();
	if (isEditing && !content?.value && description?.value) {
		return (
			<ComponentPlaceholder
				componentName="InspirationLandscape"
				description="The content field should be used instead of the description field"
				className={getEditorMargin(params)}
			/>
		);
	}

	// This should require a content field as well, but since we want to use
	// description as a fallback, we can't enforce it for now
	if (!primaryImg || !heading?.value) {
		return (
			<ComponentPlaceholder
				componentName="InspirationLandscape"
				description="Requires a primary image and a heading"
				className={getEditorMargin(params)}
			/>
		);
	}

	const video = getAsset('Source Copy', fields.video);
	const mainLinks = [primaryLink, secondaryLink, tertiaryLink, quaternaryLink]
		.map((link) => getValidSitecoreLink(link?.value))
		.filter(is.truthy);

	return (
		<InspirationLandscape
			backgroundColor={fields.backgroundColor?.fields?.colorHex?.value}
			basePriceTextBackground={
				fields.textBackgroundColor?.fields?.colorHex?.value
			}
			campaignBubbleLabel={fields.campaignBubbleLabel?.value}
			campaignBubbleSale={fields.campaignBubbleSale?.value}
			campaignBubbleText={fields.campaignBubbleText?.value}
			className={getEditorMargin(params)}
			gtmItemListId="block"
			gtmItemListName={`Block: ${datasourceName ?? 'InspirationLandscapeUnknown'}`}
			heading={heading}
			headingLevel={params?.heading ?? 'h2'}
			id={params?.anchor}
			isMirrored={fields.mirror?.value}
			isWide={fields.isWide?.value}
			imageAlt={primaryImg.alt}
			imageSrc={primaryImg.src}
			links={mainLinks}
			preventAutoplay={fields.preventAutoplay?.value}
			primaryLinkHref={
				primaryLink?.value.linktype === 'popover'
					? undefined
					: primaryLink?.value.href
			}
			productsRequestBody={fields.body}
			productsDisplayType={
				fields.isCampaignSpinner?.value ? 'campaignSpinner' : 'shopByMedia'
			}
			tallImageAlt={primaryImgTall?.alt}
			tallImageSrc={primaryImgTall?.src}
			// `description` will be removed, and it's all strings anyway, typing is just a hint for us.
			text={content?.value ? content : (description as Field<HTMLString>)}
			verticalAlign={verticalAlign?.value}
			video={video}
		/>
	);
}
InspirationLandscapeBlock.displayName = 'InspirationLandscapeBlock';
